import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Smartphone, Search, BatteryFull, Wrench, Sparkles, History, Trash2,
  Printer, Copy, ArrowLeft, FileText, Image as ImageIcon, FileDown,
  Pencil, Eye,
} from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const REPAIR_KEYS = [
  ["repair_board", "Reparo de Placa"],
  ["repair_screen", "Troca de Tela"],
  ["repair_battery", "Troca de Bateria"],
  ["repair_back", "Troca de Tampa Traseira"],
  ["repair_chassis", "Troca de Carcaça"],
  ["repair_dock", "Troca de Conector de Carga"],
  ["repair_lens", "Troca de Lente da Câmera"],
  ["repair_back_cam", "Troca de Câmera Traseira"],
  ["repair_front_cam", "Troca de Câmera Frontal"],
  ["repair_speaker", "Troca de Alto-Falante"],
  ["repair_faceid", "Reparo Face ID"],
  ["repair_button", "Troca de Botão"],
];

const BATTERY_BANDS = [
  { key: "95+", label: "95% ou superior", discount: 0 },
  { key: "90-94", label: "90% a 94%", discount: 50 },
  { key: "85-89", label: "85% a 89%", discount: 80 },
  { key: "80-84", label: "80% a 84%", discount: 130 },
  { key: "<80", label: "Abaixo de 80%", discount: 0, forceBattery: true },
];

const CONDITIONS = [
  { key: "perfect", label: "Perfeito", field: "price_perfect" },
  { key: "good", label: "Bom", field: "price_good" },
  { key: "regular", label: "Regular", field: "price_regular" },
  { key: "bad", label: "Ruim", field: "price_bad" },
];

const AUTO_OBSERVATION = `Esta avaliação foi realizada com base nas condições informadas no momento da análise.

Os custos de reparo apresentados são estimativas cadastradas pelo banco de pesquisas do sistema.

Caso haja necessidade de manutenção ou substituição de componentes, quando selecionado na avaliação os custos serão de responsabilidade da empresa que adquirir ou receber o aparelho.`;

export default function Avaliador() {
  const [tab, setTab] = useState("avaliar");
  const [models, setModels] = useState([]);
  const [history, setHistory] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [storeLogo, setStoreLogo] = useState("");

  // Estado da avaliação atual
  const [model, setModel] = useState(null);
  const [modelSearch, setModelSearch] = useState("");
  const [battery, setBattery] = useState(null);
  const [condition, setCondition] = useState(null);
  const [repairs, setRepairs] = useState({});
  const [customRepairLines, setCustomRepairLines] = useState([]);
  const [customRepairChecked, setCustomRepairChecked] = useState({});
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSel, setCustomerSel] = useState({ id: "", name: "", phone: "" });
  const [observation, setObservation] = useState("");
  const [resultMode, setResultMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewItem, setViewItem] = useState(null); // avaliação para visualizar
  const [busy, setBusy] = useState(false);

  const resultRef = useRef(null);
  const viewRef = useRef(null);

  const load = async () => {
    const [m, h] = await Promise.all([
      api.get("/evaluator/models"),
      api.get("/evaluator/evaluations").catch(() => ({ data: [] })),
    ]);
    setModels(m.data || []);
    setHistory(h.data || []);
    try {
      const s = await api.get("/store");
      setStoreName(s.data?.name || "");
      setStoreLogo(s.data?.logo || "");
    } catch { /* ignore */ }
    try {
      const c = await api.get("/customers");
      setCustomers(c.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const modelMatches = useMemo(() => {
    if (!modelSearch) return models;
    const q = modelSearch.toLowerCase();
    return models.filter(m => m.name.toLowerCase().includes(q));
  }, [models, modelSearch]);

  const selectModel = (m) => {
    setModel(m);
    setBattery(null);
    setCondition(null);
    setRepairs({});
    setCustomRepairLines([]);
    setCustomRepairChecked({});
    setResultMode(false);
  };

  const onBattery = (b) => {
    setBattery(b);
    if (b.forceBattery) {
      setRepairs(r => ({ ...r, repair_battery: true }));
    }
  };

  const selectedRepairs = useMemo(() => {
    if (!model) return [];
    const out = [];
    for (const [k, label] of REPAIR_KEYS) {
      if (repairs[k]) out.push({ key: k, name: label, price: Number(model[k] || 0) });
    }
    for (const cr of (model.custom_repairs || [])) {
      if (customRepairChecked[cr.id]) {
        out.push({ key: "custom", name: cr.name, price: Number(cr.price || 0) });
      }
    }
    for (const line of customRepairLines) {
      if (!line.name?.trim()) continue;
      out.push({ key: "custom", name: line.name.trim(), price: Number(line.price || 0) });
    }
    return out;
  }, [model, repairs, customRepairChecked, customRepairLines]);

  const repairsTotal = selectedRepairs.reduce((s, r) => s + Number(r.price || 0), 0);
  const conditionValue = condition && model ? Number(model[CONDITIONS.find(c => c.key === condition).field] || 0) : 0;
  const batteryDiscount = battery?.discount || 0;
  const baseAfterBattery = conditionValue - batteryDiscount;
  const finalValue = baseAfterBattery - repairsTotal;
  const isNegative = finalValue < 0;

  const computeStateValue = (cond) => {
    if (!model) return 0;
    const base = Number(model[CONDITIONS.find(c => c.key === cond).field] || 0);
    return base - batteryDiscount;
  };

  const doEvaluate = () => {
    if (!model) return toast.error("Selecione um modelo");
    if (!battery) return toast.error("Selecione a saúde da bateria");
    if (!condition) return toast.error("Selecione o estado do aparelho");
    setResultMode(true);
    setTimeout(() => window.scrollTo({ top: 9999, behavior: "smooth" }), 50);
  };

  const reset = () => {
    setModel(null); setBattery(null); setCondition(null);
    setRepairs({}); setCustomRepairLines([]); setCustomRepairChecked({});
    setResultMode(false); setModelSearch("");
    setEditingId(null);
    setCustomerSel({ id: "", name: "", phone: "" });
    setObservation("");
  };

  const buildPayload = () => ({
    model_id: model.id,
    model_name: model.name,
    model_photo: model.photo || "",
    battery_band: battery.key,
    battery_discount: battery.discount,
    condition,
    condition_value: conditionValue,
    repairs: selectedRepairs,
    customer_id: customerSel.id || null,
    customer_name: customerSel.name || "",
    customer_phone: customerSel.phone || "",
    observation: observation || AUTO_OBSERVATION,
  });

  const saveEvaluation = async () => {
    if (!model || !battery || !condition) return;
    try {
      if (editingId) {
        await api.put(`/evaluator/evaluations/${editingId}`, buildPayload());
        toast.success("Avaliação atualizada");
      } else {
        await api.post("/evaluator/evaluations", buildPayload());
        toast.success("Avaliação salva no histórico");
      }
      setSaveDialogOpen(false);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const removeFromHistory = async (id) => {
    if (!window.confirm("Excluir esta avaliação do histórico?")) return;
    await api.delete(`/evaluator/evaluations/${id}`);
    toast.success("Excluída"); load();
  };

  const duplicateHistory = async (id) => {
    try {
      await api.post(`/evaluator/evaluations/${id}/duplicate`);
      toast.success("Duplicada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ── Carregar avaliação no formulário para EDITAR ────────────────────────
  const loadForEdit = (h) => {
    const m = models.find(x => x.id === h.model_id);
    if (!m) {
      toast.error("Modelo dessa avaliação não está mais cadastrado.");
      return;
    }
    setModel(m);
    setBattery(BATTERY_BANDS.find(b => b.key === h.battery_band) || null);
    setCondition(h.condition);

    // remontar repairs
    const repairsMap = {};
    const customChecked = {};
    const adhoc = [];
    const masterCustoms = m.custom_repairs || [];
    for (const r of (h.repairs || [])) {
      if (r.key && r.key.startsWith("repair_")) {
        repairsMap[r.key] = true;
      } else {
        const match = masterCustoms.find(cr =>
          cr.name === r.name && Number(cr.price) === Number(r.price)
        );
        if (match) customChecked[match.id] = true;
        else adhoc.push({ name: r.name, price: Number(r.price || 0) });
      }
    }
    setRepairs(repairsMap);
    setCustomRepairChecked(customChecked);
    setCustomRepairLines(adhoc);

    setCustomerSel({
      id: h.customer_id || "",
      name: h.customer_name || "",
      phone: h.customer_phone || "",
    });
    setObservation(h.observation || AUTO_OBSERVATION);
    setEditingId(h.id);
    setResultMode(true);
    setTab("avaliar");
    setTimeout(() => window.scrollTo({ top: 9999, behavior: "smooth" }), 100);
  };

  // ── Captura do card para foto/PDF/print ─────────────────────────────────
  const captureNode = async (node) => {
    if (!node) return null;
    try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch {}
    await new Promise(r => setTimeout(r, 150));
    const htmlEl = document.documentElement;
    const wasDark = htmlEl.classList.contains("dark");
    if (wasDark) htmlEl.classList.remove("dark");
    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      return await html2canvas(node, {
        scale: 3, backgroundColor: "#ffffff", useCORS: true,
        letterRendering: true, logging: false,
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
      });
    } finally {
      if (wasDark) htmlEl.classList.add("dark");
    }
  };

  const fileNameFor = (data) => {
    const safe = (data?.model_name || model?.name || "iphone").replace(/[^\w-]+/g, "_");
    const cust = (data?.customer_name || customerSel.name || "").replace(/[^\w-]+/g, "_");
    return `avaliacao-${safe}${cust ? "-" + cust : ""}`;
  };

  const downloadImg = async (node, data) => {
    setBusy(true);
    try {
      const c = await captureNode(node);
      if (!c) return;
      const filename = `${fileNameFor(data)}.png`;
      const blob = await new Promise(res => c.toBlob(res, "image/png", 1.0));
      if (!blob) throw new Error("Falha ao gerar PNG");

      // iOS detection: usa Web Share API para abrir tela nativa (Salvar em Fotos, etc.)
      const ua = navigator.userAgent || "";
      const isIOS = /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      if (isIOS && typeof navigator.canShare === "function") {
        try {
          const file = new File([blob], filename, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: "Avaliação de iPhone" });
            toast.success("Compartilhamento aberto");
            return;
          }
        } catch (shareErr) {
          if (shareErr?.name === "AbortError") { toast("Cancelado"); return; }
        }
      }

      // Android / desktop: download direto em alta qualidade
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("Imagem salva");
    } catch (e) { console.error("[Avaliador savePhoto]", e); toast.error("Erro foto: " + (e?.message || e)); }
    finally { setBusy(false); }
  };

  const downloadPDF = async (node, data) => {
    setBusy(true);
    try {
      const canvas = await captureNode(node);
      if (!canvas) return;
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();   // 210
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297
      const margin = 6, maxWidth = pageWidth - margin * 2, maxHeight = pageHeight - margin * 2;
      const mmPerPx = maxWidth / canvas.width;
      let renderW = maxWidth;
      let renderH = canvas.height * mmPerPx;
      // SEMPRE uma única página: se a altura excede A4, escala proporcionalmente para caber.
      if (renderH > maxHeight) {
        const k = maxHeight / renderH;
        renderH = maxHeight;
        renderW = maxWidth * k;
      }
      const x = margin + (maxWidth - renderW) / 2;
      const y = margin + (maxHeight - renderH) / 2;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, renderW, renderH);
      pdf.save(`${fileNameFor(data)}.pdf`);
      toast.success("PDF gerado");
    } catch (e) { console.error("[Avaliador savePdf]", e); toast.error("Erro PDF: " + (e?.message || e)); }
    finally { setBusy(false); }
  };

  const printNode = async (node, data) => {
    if (!node) return;
    setBusy(true);
    try {
      const canvas = await captureNode(node);
      if (!canvas || canvas.width < 50) { toast.error("Aguarde carregar"); return; }
      const dataUrl = canvas.toDataURL("image/png");
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!doctype html><html><head><title>${fileNameFor(data)}</title>
        <style>
          @page { size: A4; margin: 6mm; }
          html, body { margin: 0; padding: 0; background: #fff; width: 210mm; height: 297mm; }
          body { display: flex; align-items: center; justify-content: center; overflow: hidden; }
          .page { width: 198mm; height: 285mm; display: flex; align-items: center; justify-content: center; }
          img { max-width: 198mm; max-height: 285mm; width: auto; height: auto; object-fit: contain;
                page-break-inside: avoid; break-inside: avoid; }
        </style></head>
        <body><div class="page"><img src="${dataUrl}"/></div></body></html>`);
      doc.close();
      const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
      const triggerPrint = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch {}
        setTimeout(cleanup, 1500);
      };
      const img = doc.querySelector("img");
      if (img && !img.complete) img.onload = () => setTimeout(triggerPrint, 100);
      else setTimeout(triggerPrint, 200);
    } finally { setBusy(false); }
  };

  // ── Renderiza um card de resultado (usado para avaliar e para visualizar) ─
  // NOTE: ResultCard é declarado FORA do componente Avaliador (no final do arquivo)
  // para evitar remontagem do DOM a cada render do pai (que tornava o ref obsoleto
  // e quebrava o html2canvas com "Unable to find element in cloned iframe").

  const currentData = {
    model_name: model?.name,
    model_photo: model?.photo,
    condition,
    condition_value: conditionValue,
    battery_band: battery?.key,
    battery_discount: batteryDiscount,
    repairs: selectedRepairs,
    final_value: finalValue,
    customer_name: customerSel.name,
    customer_phone: customerSel.phone,
    observation: observation || AUTO_OBSERVATION,
  };

  return (
    <div className="space-y-6" data-testid="avaliador-page">
      <div>
        <p className="label-tiny mb-2 inline-flex items-center gap-1"><Smartphone size={11}/> GE Avaliações</p>
        <h1 className="font-display text-4xl tracking-tight">Avaliador de iPhone</h1>
        <p className="text-sm text-neutral-500 mt-1">Avaliação padronizada e profissional para compra de aparelhos.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="avaliar" data-testid="tab-avaliar"><Sparkles size={14} className="mr-1"/>Avaliar</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico"><History size={14} className="mr-1"/>Histórico ({history.length}/15)</TabsTrigger>
        </TabsList>

        <TabsContent value="avaliar" className="mt-4 space-y-5">
          {/* 1. Seleção do modelo */}
          <Card className="p-4 border-[#E5E5E5] shadow-none">
            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">1. Modelo</p>
            {model ? (
              <div className="flex items-center gap-3">
                {model.photo && <img src={model.photo} alt="" className="w-14 h-14 rounded-md object-cover"/>}
                <div className="flex-1"><div className="font-medium">{model.name}</div></div>
                <Button variant="outline" size="sm" onClick={()=>setModel(null)} data-testid="change-model-btn">Trocar</Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
                  <Input placeholder="Buscar modelo..." className="pl-9" value={modelSearch} onChange={e=>setModelSearch(e.target.value)} data-testid="model-search"/>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                  {modelMatches.map(m => (
                    <button key={m.id} onClick={()=>selectModel(m)} className="border rounded-lg p-2 text-left hover:border-[#0A0A0A] transition" data-testid={`pick-model-${m.id}`}>
                      <div className="flex items-center gap-2">
                        {m.photo
                          ? <img src={m.photo} alt="" className="w-10 h-10 rounded object-cover"/>
                          : <div className="w-10 h-10 rounded bg-neutral-100 flex items-center justify-center"><Smartphone size={14} className="text-neutral-400"/></div>}
                        <div className="text-xs font-medium truncate flex-1">{m.name}</div>
                      </div>
                    </button>
                  ))}
                  {modelMatches.length === 0 && <p className="col-span-full text-xs text-neutral-400 italic">Nenhum modelo cadastrado pelo administrador ainda.</p>}
                </div>
              </>
            )}
          </Card>

          {/* 2. Bateria */}
          {model && (
            <Card className="p-4 border-[#E5E5E5] shadow-none">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2 inline-flex items-center gap-1"><BatteryFull size={11}/> 2. Saúde da bateria</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {BATTERY_BANDS.map(b => (
                  <button key={b.key} onClick={()=>onBattery(b)}
                    className={`border rounded-lg p-3 text-left transition ${battery?.key === b.key ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-neutral-400"}`}
                    data-testid={`battery-${b.key}`}>
                    <div className="text-xs font-medium">{b.label}</div>
                    {b.discount > 0 && <div className={`text-[10px] mt-0.5 ${battery?.key === b.key ? "text-neutral-300" : "text-red-600"}`}>− {brl(b.discount)}</div>}
                    {b.forceBattery && <div className={`text-[10px] mt-0.5 ${battery?.key === b.key ? "text-amber-300" : "text-amber-700"}`}>+ obriga troca</div>}
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* 3. Estado */}
          {model && battery && (
            <Card className="p-4 border-[#E5E5E5] shadow-none">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">3. Estado do aparelho</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CONDITIONS.map(c => {
                  const v = computeStateValue(c.key);
                  return (
                    <button key={c.key} onClick={()=>setCondition(c.key)}
                      className={`border rounded-lg p-3 text-center transition ${condition === c.key ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-neutral-400"}`}
                      data-testid={`condition-${c.key}`}>
                      <div className="text-xs font-medium">{c.label}</div>
                      <div className={`text-sm font-display mt-1 ${condition === c.key ? "" : "text-[#0A0A0A]"}`}>{brl(v)}</div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          {/* 4. Reparos */}
          {model && condition && (
            <Card className="p-4 border-[#E5E5E5] shadow-none">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3 inline-flex items-center gap-1"><Wrench size={11}/> 4. O aparelho necessita de algum reparo?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {REPAIR_KEYS.map(([k, label]) => {
                  const price = Number(model[k] || 0);
                  return (
                    <label key={k} className="flex items-start gap-2 border border-[#E5E5E5] rounded-md p-2 cursor-pointer hover:bg-neutral-50" data-testid={`repair-${k}`}>
                      <Checkbox checked={!!repairs[k]} onCheckedChange={(v)=>setRepairs(r=>({...r, [k]: !!v}))}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium">{label}</div>
                        <div className="text-[10px] text-red-600">− {brl(price)}</div>
                      </div>
                    </label>
                  );
                })}
                {(model.custom_repairs || []).map(cr => (
                  <label key={cr.id} className="flex items-start gap-2 border border-[#E5E5E5] rounded-md p-2 cursor-pointer hover:bg-neutral-50">
                    <Checkbox checked={!!customRepairChecked[cr.id]} onCheckedChange={(v)=>setCustomRepairChecked(s=>({...s, [cr.id]: !!v}))}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium">{cr.name} <span className="text-[9px] text-neutral-400">(extra)</span></div>
                      <div className="text-[10px] text-red-600">− {brl(cr.price)}</div>
                    </div>
                  </label>
                ))}
              </div>
              {/* Outro reparo (livre) */}
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={()=>setCustomRepairLines([...customRepairLines, {name:"", price:0}])} data-testid="add-other-repair">
                  + Outro reparo (avulso)
                </Button>
                {customRepairLines.map((line, idx) => (
                  <div key={idx} className="flex gap-2 mt-2 items-end">
                    <div className="flex-1"><Label className="text-[10px]">Nome</Label><Input value={line.name} onChange={e=>setCustomRepairLines(L=>L.map((x,i)=>i===idx?{...x,name:e.target.value}:x))}/></div>
                    <div className="w-32"><Label className="text-[10px]">Valor</Label><CurrencyInput value={line.price} onChange={(v)=>setCustomRepairLines(L=>L.map((x,i)=>i===idx?{...x,price:v}:x))}/></div>
                    <Button size="sm" variant="ghost" onClick={()=>setCustomRepairLines(L=>L.filter((_,i)=>i!==idx))}><Trash2 size={12}/></Button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {model && condition && !resultMode && (
            <div className="text-center">
              <Button size="lg" onClick={doEvaluate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="do-evaluate-btn">
                <Sparkles size={16} className="mr-2"/>{editingId ? "Recalcular Avaliação" : "Fazer Avaliação"}
              </Button>
            </div>
          )}

          {resultMode && model && condition && (
            <div className="mt-6 space-y-4">
              <ResultCard data={currentData} refOverride={resultRef} storeName={storeName} storeLogo={storeLogo}/>

              <div className="flex flex-wrap gap-2 print:hidden">
                <Button variant="outline" disabled={busy} onClick={() => downloadImg(resultRef.current, currentData)} data-testid="btn-save-img">
                  <ImageIcon size={14} className="mr-1"/> Salvar Foto
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => downloadPDF(resultRef.current, currentData)} data-testid="btn-save-pdf">
                  <FileDown size={14} className="mr-1"/> Salvar PDF
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => printNode(resultRef.current, currentData)} data-testid="btn-print">
                  <Printer size={14} className="mr-1"/> Imprimir
                </Button>
                <Button
                  onClick={() => { if (!editingId) setObservation(AUTO_OBSERVATION); setSaveDialogOpen(true); }}
                  className="bg-[#0A0A0A] hover:bg-black text-white"
                  data-testid="btn-save-eval"
                >
                  <FileText size={14} className="mr-1"/> {editingId ? "Salvar alterações" : "Salvar avaliação"}
                </Button>
                <Button variant="ghost" onClick={reset}><ArrowLeft size={14} className="mr-1"/> Nova avaliação</Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          {history.length === 0
            ? <p className="text-center py-16 text-sm text-neutral-500">Nenhuma avaliação salva.</p>
            : (
              <div className="space-y-2">
                {history.map(h => (
                  <Card key={h.id} className="p-3 border-[#E5E5E5] shadow-none flex items-center gap-3 flex-wrap" data-testid={`history-${h.id}`}>
                    {h.model_photo && <img src={h.model_photo} alt="" className="w-12 h-12 rounded object-cover"/>}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{h.model_name}</div>
                      <div className="text-[11px] text-neutral-500">{h.customer_name || "Sem cliente"} · {new Date(h.created_at).toLocaleString("pt-BR")}</div>
                    </div>
                    <div className={`font-display text-lg ${h.final_value < 0 ? "text-red-600" : ""}`}>{brl(h.final_value)}</div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={()=>setViewItem(h)} title="Visualizar" data-testid={`view-${h.id}`}><Eye size={12}/></Button>
                      <Button size="sm" variant="ghost" onClick={()=>loadForEdit(h)} title="Editar" data-testid={`edit-${h.id}`}><Pencil size={12}/></Button>
                      <Button size="sm" variant="ghost" onClick={()=>duplicateHistory(h.id)} title="Duplicar" data-testid={`duplicate-${h.id}`}><Copy size={12}/></Button>
                      <Button size="sm" variant="ghost" onClick={()=>removeFromHistory(h.id)} title="Excluir" data-testid={`delete-${h.id}`}><Trash2 size={12}/></Button>
                    </div>
                  </Card>
                ))}
              </div>
            )
          }
        </TabsContent>
      </Tabs>

      {/* Save dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Atualizar avaliação" : "Vincular cliente e salvar"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cliente (selecione ou digite um novo)</Label>
              <Input
                list="customer-list"
                value={customerSel.name}
                onChange={(e) => {
                  const v = e.target.value;
                  const c = customers.find(x => x.name === v);
                  setCustomerSel({ id: c?.id || "", name: v, phone: c?.phone || customerSel.phone });
                }}
                placeholder="Nome do cliente"
                data-testid="customer-input"
              />
              <datalist id="customer-list">
                {customers.map(c => <option key={c.id} value={c.name}/>)}
              </datalist>
            </div>
            <div><Label>Telefone (opcional)</Label><Input value={customerSel.phone} onChange={e=>setCustomerSel({...customerSel, phone: e.target.value})}/></div>
            <div><Label>Observações</Label>
              <textarea value={observation} onChange={e=>setObservation(e.target.value)} className="w-full text-sm border rounded p-2" rows={4}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSaveDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveEvaluation} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="confirm-save-eval">
              {editingId ? "Atualizar" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Visualizar avaliação</DialogTitle></DialogHeader>
          {viewItem && (
            <>
              <ResultCard data={viewItem} refOverride={viewRef} storeName={storeName} storeLogo={storeLogo}/>
              <DialogFooter className="flex-wrap gap-2 mt-4">
                <Button variant="outline" disabled={busy} onClick={() => downloadImg(viewRef.current, viewItem)} data-testid="view-btn-save-img">
                  <ImageIcon size={14} className="mr-1"/> Salvar Foto
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => downloadPDF(viewRef.current, viewItem)} data-testid="view-btn-save-pdf">
                  <FileDown size={14} className="mr-1"/> Salvar PDF
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => printNode(viewRef.current, viewItem)} data-testid="view-btn-print">
                  <Printer size={14} className="mr-1"/> Imprimir
                </Button>
                <Button variant="ghost" onClick={() => setViewItem(null)}>Fechar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── ResultCard fora do componente Avaliador (evita remount em cada render) ─
const ResultCard = React.forwardRef(({ data, refOverride, storeName, storeLogo }, ref) => {
  const m = data;
  const condLabel = CONDITIONS.find(c => c.key === m.condition)?.label || "";
  const bat = BATTERY_BANDS.find(b => b.key === m.battery_band) || { label: "" };
  const cv = Number(m.condition_value || 0);
  const bd = Number(m.battery_discount || 0);
  const reps = m.repairs || [];
  const rt = reps.reduce((s, r) => s + Number(r.price || 0), 0);
  const fv = Number(m.final_value !== undefined ? m.final_value : (cv - bd - rt));
  const neg = fv < 0;
  return (
    <Card
      ref={refOverride || ref}
      className="p-6 border-2 border-[#0A0A0A] shadow-2xl bg-white"
      data-testid="evaluation-result"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {m.model_photo && <img src={m.model_photo} alt={m.model_name} className="w-24 h-24 rounded-lg object-cover"/>}
          <div>
            <p className="text-xs uppercase tracking-widest text-neutral-500">Avaliação de iPhone</p>
            <h2 className="font-display text-2xl">{m.model_name}</h2>
            <p className="text-xs text-neutral-500 mt-1">
              {m.created_at ? new Date(m.created_at).toLocaleString("pt-BR") : new Date().toLocaleString("pt-BR")}
            </p>
            {m.customer_name && (
              <p className="text-[11px] text-neutral-600 mt-0.5">Cliente: {m.customer_name}{m.customer_phone ? ` · ${m.customer_phone}` : ""}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {storeLogo && <img src={storeLogo} alt="logo" className="w-14 h-14 rounded-full object-cover"/>}
          <div className="flex flex-col items-end text-right">
            <p className="text-sm font-medium leading-tight">{storeName || "Sua loja"}</p>
            <p className="text-[10px] text-neutral-500 leading-tight">GE Avaliações de iPhones</p>
          </div>
        </div>
      </div>

      <div className="mt-6 p-5 rounded-xl bg-neutral-50 text-center">
        <p className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Valor Final Oferecido</p>
        <p
          className={`font-display text-5xl tracking-tight ${neg ? "text-red-600" : "text-[#0A0A0A]"}`}
          data-testid="evaluation-final-value"
        >
          {brl(fv)}
        </p>
        {neg && <p className="text-sm text-red-600 mt-2 font-medium">Valor de troca não recomendado.</p>}
      </div>

      <div className="mt-5">
        <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Detalhamento</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span>Valor base ({condLabel}) e saúde da bateria{bat.label ? ` (${bat.label})` : ""}</span>
            <span>{brl(cv - bd)}</span>
          </div>
        </div>
      </div>

      {reps.length > 0 && (
        <div className="mt-4">
          <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Reparos necessários</p>
          <div className="space-y-1 text-sm">
            {reps.map((r, i) => (
              <div key={i} className="flex justify-between border-b border-dashed border-neutral-200 py-1">
                <span>{r.name}</span><span className="text-red-600">− {brl(r.price)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 font-medium">
              <span>Total de reparos</span><span className="text-red-600">− {brl(rt)}</span>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] text-neutral-500 leading-snug mt-5 whitespace-pre-line">
        {m.observation || AUTO_OBSERVATION}
      </p>
    </Card>
  );
});
ResultCard.displayName = "ResultCard";

